import { useState } from 'react';
import { Title, Button, Group, Stack, TextInput, ActionIcon, Modal, Text, Badge, SegmentedControl } from '@mantine/core';
import { IconPlus, IconSearch, IconTrash, IconEye } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useNavigate } from 'react-router-dom';
import { useCompanies, useDeleteCompany } from '@/hooks/useCompanies';
import { notifications } from '@mantine/notifications';
import type { CompanyWithDetails, CompanyStatus } from '@/types/company';
import { isOpenDemo } from '@/types/company';

/** Filter value spanning every demo status that still needs attention. */
const OPEN_DEMOS_FILTER = 'open-demos';

const STATUS_LABELS: Record<CompanyStatus, string> = {
  DEMO_REQUESTED: 'Neu',
  DEMO_CONTACTED: 'Kontaktiert',
  DEMO_SCHEDULED: 'Termin',
  DEMO_COMPLETED: 'Abgeschlossen',
  DEMO_DECLINED: 'Abgelehnt',
  pending: 'Registriert',
  active: 'Aktiv',
  inactive: 'Inaktiv',
};

const STATUS_COLORS: Record<CompanyStatus, string> = {
  DEMO_REQUESTED: 'orange',
  DEMO_CONTACTED: 'blue',
  DEMO_SCHEDULED: 'cyan',
  DEMO_COMPLETED: 'teal',
  DEMO_DECLINED: 'red',
  pending: 'indigo',
  active: 'green',
  inactive: 'gray',
};

interface CompanyListPageProps {
  filterDemo?: boolean;
}

export function CompanyListPage({ filterDemo }: CompanyListPageProps = {}) {
  const navigate = useNavigate();
  const { data: companies, isLoading } = useCompanies();
  const deleteCompany = useDeleteCompany();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteTarget, setDeleteTarget] = useState<CompanyWithDetails | null>(null);

  const baseCompanies = filterDemo
    ? (companies ?? []).filter((c) => c.status.startsWith('DEMO_'))
    : (companies ?? []);

  const filtered = baseCompanies.filter((c) => {
    const matchesSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.legalForm.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase());
    // 'open-demos' spans several statuses, so it cannot be a plain equality check
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === OPEN_DEMOS_FILTER ? isOpenDemo(c.status) : c.status === statusFilter);
    return matchesSearch && matchesStatus;
  });

  // Counts only open demos, matching what the 'demo' filter shows. Counting every
  // DEMO_* status here while the filter matched a single one made a completed demo
  // raise the badge and then yield an empty list.
  const demoCount = (companies ?? []).filter((c) => isOpenDemo(c.status)).length;
  const counts = {
    all: baseCompanies.length,
    DEMO_REQUESTED: baseCompanies.filter((c) => c.status === 'DEMO_REQUESTED').length,
    pending: baseCompanies.filter((c) => c.status === 'pending').length,
    active: baseCompanies.filter((c) => c.status === 'active').length,
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteCompany.mutate(deleteTarget.id, {
      onSuccess: () => {
        notifications.show({ title: 'Gelöscht', message: `${deleteTarget.name} wurde gelöscht.`, color: 'green' });
        setDeleteTarget(null);
      },
      onError: () => {
        notifications.show({ title: 'Fehler', message: 'Unternehmen konnte nicht gelöscht werden.', color: 'red' });
      },
    });
  };

  const filterOptions = filterDemo
    ? [
        { label: `Alle (${counts.all})`, value: 'all' },
        { label: `Neu (${counts.DEMO_REQUESTED})`, value: 'DEMO_REQUESTED' },
      ]
    : [
        { label: `Alle (${counts.all})`, value: 'all' },
        { label: `Demo (${demoCount})`, value: OPEN_DEMOS_FILTER },
        { label: `Registriert (${counts.pending})`, value: 'pending' },
        { label: `Aktiv (${counts.active})`, value: 'active' },
      ];

  return (
    <Stack gap="md">
      {!filterDemo && (
        <Group justify="space-between">
          <Title order={2}>Unternehmen</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/companies/new')}>
            Neues Unternehmen
          </Button>
        </Group>
      )}

      <Group>
        <SegmentedControl
          value={statusFilter}
          onChange={setStatusFilter}
          data={filterOptions}
        />
        <TextInput
          placeholder="Suchen..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={filtered}
        fetching={isLoading}
        columns={[
          { accessor: 'name', title: 'Name', sortable: true },
          {
            accessor: 'status',
            title: 'Status',
            sortable: true,
            render: (company) => (
              <Badge color={STATUS_COLORS[company.status] ?? 'gray'} variant="light">
                {STATUS_LABELS[company.status] ?? company.status}
              </Badge>
            ),
          },
          { accessor: 'email', title: 'E-Mail' },
          { accessor: 'billingAddress.city', title: 'Ort', render: (company) => company.billingAddress?.city || '-' },
          {
            accessor: 'source',
            title: 'Quelle',
            render: (company) => company.source === 'restaurant' ? 'Restaurant' : company.source === 'haendler' ? 'Händler' : '-',
          },
          {
            accessor: 'actions',
            title: '',
            width: 100,
            render: (company) => (
              <Group gap="xs" wrap="nowrap">
                <ActionIcon variant="subtle" onClick={() => navigate(`/companies/${company.id}`)}>
                  <IconEye size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(company)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        noRecordsText="Keine Unternehmen gefunden"
      />

      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Unternehmen löschen">
        <Stack>
          <Text>
            Möchten Sie <strong>{deleteTarget?.name}</strong> wirklich löschen? Alle zugehörigen
            Shops und Inhaber werden ebenfalls gelöscht.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Abbrechen
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteCompany.isPending}>
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
